import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Grid,
  LinearProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import BusinessIcon from '@mui/icons-material/Business'
import ForumIcon from '@mui/icons-material/Forum'
import PermMediaIcon from '@mui/icons-material/PermMedia'
import StarIcon from '@mui/icons-material/Star'
import API from '../../services/API'
import PageHeader from '../kit/PageHeader'
import SectionCard from '../kit/SectionCard'
import StatTile from '../kit/StatTile'
import { formatDate } from '../kit/helpers'
import { COMMENT_QUESTIONS } from './trainingMeta'

const SERIES = '#1E7F88'
const TRACK = '#EDF1F5'

const percent = (part, whole) => (whole ? Math.round((part / whole) * 100) : 0)

// One horizontal bar per row on a shared scale, each labelled with its
// count, so the figures never depend on reading the bar.
const BarList = ({ rows, total, unit = 'responses' }) => {
  const max = Math.max(...rows.map((row) => row.count), 1)
  return (
    <Stack gap={1} component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
      {rows.map((row) => (
        <Tooltip key={row.label} title={`${row.label}: ${row.count} of ${total} ${unit} (${percent(row.count, total)}%)`} arrow>
          <Stack component="li" direction="row" alignItems="center" gap={1.5}>
            <Typography variant="body2" sx={{ width: { xs: 110, sm: 150 }, flexShrink: 0 }}>
              {row.label}
            </Typography>
            <Box sx={{ flex: 1, height: 12, bgcolor: TRACK, borderRadius: '0 4px 4px 0' }}>
              <Box
                sx={{
                  width: `${(row.count / max) * 100}%`,
                  height: '100%',
                  bgcolor: SERIES,
                  borderRadius: '0 4px 4px 0',
                }}
              />
            </Box>
            <Typography variant="body2" fontWeight={600} sx={{ width: 76, textAlign: 'right', flexShrink: 0 }}>
              {row.count} ({percent(row.count, total)}%)
            </Typography>
          </Stack>
        </Tooltip>
      ))}
    </Stack>
  )
}

const ratingRows = (spread = []) =>
  [1, 2, 3, 4, 5].map((rating, index) => ({
    label: `${rating} ${rating === 1 ? '(lowest)' : rating === 5 ? '(highest)' : ''}`.trim(),
    count: spread[index] ?? 0,
  }))

// Anonymous learner feedback across every tenant for one course.
const FeedbackReport = () => {
  const [versions, setVersions] = useState([])
  const [code, setCode] = useState('')
  const [version, setVersion] = useState('')
  const [report, setReport] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    API.get('/admincrm/training/courses').then((res) => {
      if (res.status !== 200) {
        setError(res.data?.error ?? 'Unable to load the courses')
        return
      }
      setVersions(res.data)
      if (res.data[0]) setCode(res.data[0].code)
    })
  }, [])

  const courses = useMemo(() => {
    const byCode = new Map()
    versions.forEach((entry) => {
      if (!byCode.has(entry.code)) byCode.set(entry.code, entry.title)
    })
    return [...byCode].map(([courseCode, title]) => ({ code: courseCode, title }))
  }, [versions])
  const courseVersions = versions.filter((entry) => entry.code === code)

  useEffect(() => {
    if (!code) return undefined
    let active = true
    setLoading(true)
    API.get('/admincrm/training/feedback-report', { params: { code, ...(version ? { version } : {}) } })
      .then((res) => {
        if (!active) return
        if (res.status === 200) {
          setReport(res.data)
          setError(null)
        } else {
          setReport(null)
          setError(res.data?.error ?? 'Unable to load the feedback report')
        }
      })
      .finally(() => active && setLoading(false))
    return () => {
      active = false
    }
  }, [code, version])

  const total = report?.responses ?? 0

  return (
    <Stack gap={2}>
      <PageHeader
        eyebrow="Training"
        title="Course feedback"
        subtitle="Responses to the CPD UK delegate feedback form, across all tenants. Responses are anonymous: they hold no learner name or identifier, and cannot be traced back to a person."
      />

      <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5}>
        <TextField
          select
          size="small"
          label="Course"
          value={code}
          onChange={(event) => {
            setCode(event.target.value)
            setVersion('')
          }}
          sx={{ minWidth: 280, bgcolor: '#FFFFFF' }}
        >
          {courses.map((course) => (
            <MenuItem key={course.code} value={course.code}>
              {course.code} · {course.title}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Version"
          value={version}
          onChange={(event) => setVersion(event.target.value)}
          SelectProps={{ displayEmpty: true }}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 180, bgcolor: '#FFFFFF' }}
        >
          <MenuItem value="">All versions</MenuItem>
          {courseVersions.map((entry) => (
            <MenuItem key={entry.version} value={entry.version}>
              v{entry.version} ({entry.feedbackCount} response{entry.feedbackCount === 1 ? '' : 's'})
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {loading && <LinearProgress />}
      {error && <Alert severity="error">{error}</Alert>}
      {!error && versions.length === 0 && !loading && (
        <Alert severity="info">No course versions have been published yet.</Alert>
      )}

      {report && (
        <>
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}>
              <StatTile label="Responses" value={total} icon={<ForumIcon />} />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatTile label="Tenants" value={report.tenants} icon={<BusinessIcon />} tone="#557A95" />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatTile
                label="Overall experience"
                value={report.overallExperience ?? 'n/a'}
                caption="Average, out of 5"
                icon={<StarIcon />}
                tone="#85276A"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatTile
                label="Multimedia quality"
                value={report.multimediaQuality ?? 'n/a'}
                caption="Average, out of 5"
                icon={<PermMediaIcon />}
                tone="#1F3D7C"
              />
            </Grid>
          </Grid>

          {total === 0 ? (
            <Alert severity="info">No feedback has been submitted for this selection yet.</Alert>
          ) : (
            <>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <SectionCard title="Overall experience" subtitle="Responses by rating" sx={{ height: '100%' }}>
                    <BarList rows={ratingRows(report.ratingSpread?.overallExperience)} total={total} />
                  </SectionCard>
                </Grid>
                <Grid item xs={12} md={4}>
                  <SectionCard title="Multimedia quality" subtitle="Responses by rating" sx={{ height: '100%' }}>
                    <BarList rows={ratingRows(report.ratingSpread?.multimediaQuality)} total={total} />
                  </SectionCard>
                </Grid>
                <Grid item xs={12} md={4}>
                  <SectionCard title="Time to complete" subtitle="As reported by the learner" sx={{ height: '100%' }}>
                    <BarList
                      rows={(report.completionTime ?? []).map((entry) => ({ label: entry.band, count: entry.count }))}
                      total={total}
                    />
                  </SectionCard>
                </Grid>
              </Grid>

              <SectionCard
                title="Usability statements"
                subtitle="Learners answer Yes or No to each. Two are worded negatively (rushed, video fatigue), where a low Yes is the good result."
                dense
              >
                <Box overflow="auto">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Statement</TableCell>
                        <TableCell align="right">Yes</TableCell>
                        <TableCell align="right">No</TableCell>
                        <TableCell align="right">% Yes</TableCell>
                        <TableCell sx={{ width: '25%', display: { xs: 'none', md: 'table-cell' } }} aria-hidden />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(report.usability ?? []).map((statement) => {
                        const answered = statement.yes + statement.no
                        const yesPercent = percent(statement.yes, answered)
                        return (
                          <TableRow key={statement.key}>
                            <TableCell>{statement.text}</TableCell>
                            <TableCell align="right">{statement.yes}</TableCell>
                            <TableCell align="right">{statement.no}</TableCell>
                            <TableCell align="right">
                              <b>{answered ? `${yesPercent}%` : 'n/a'}</b>
                            </TableCell>
                            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }} aria-hidden>
                              <Box sx={{ height: 10, bgcolor: TRACK, borderRadius: '0 4px 4px 0' }}>
                                <Box
                                  sx={{
                                    width: `${yesPercent}%`,
                                    height: '100%',
                                    bgcolor: SERIES,
                                    borderRadius: '0 4px 4px 0',
                                  }}
                                />
                              </Box>
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </Box>
              </SectionCard>

              {COMMENT_QUESTIONS.map((question) => {
                const comments = report.comments?.[question.field] ?? []
                return (
                  <SectionCard
                    key={question.field}
                    title={question.label}
                    subtitle={`${comments.length} comment${comments.length === 1 ? '' : 's'}`}
                  >
                    {comments.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No comments.
                      </Typography>
                    ) : (
                      <Stack gap={1.25} sx={{ maxHeight: 360, overflow: 'auto' }}>
                        {comments.map((comment, index) => (
                          <Box key={index} sx={{ borderLeft: '3px solid #E2E8F0', pl: 1.5 }}>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {comment.text}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              v{comment.version}
                              {comment.submittedAt ? ` · ${formatDate(comment.submittedAt)}` : ''}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </SectionCard>
                )
              })}
            </>
          )}
        </>
      )}
    </Stack>
  )
}

export default FeedbackReport
